/**
 * Semantic placement and depth (spec 021). Positions are the hinge point's,
 * in % of the stage (spec 006). The fractions are per orientation and do not
 * depend on a sprite's pixel size, so the Service and the Editor resolve
 * identically without image dimensions.
 */
import type { Orientation, Sprite } from '../@types';
import { getResponsivePosition } from '../utils/responsiveLayout';
import type { AtLabel, Placement } from './types';

export const ORIENTATIONS: readonly Orientation[] = ['portrait', 'landscape', 'desktop'];

const AT_LABELS: readonly AtLabel[] = ['left', 'centerLeft', 'center', 'centerRight', 'right'];

/** `at` labels as x in % of the stage width, per orientation (portrait keeps sprites further in). */
export const AT_X: Readonly<Record<Orientation, Readonly<Record<AtLabel, number>>>> = {
  desktop: { left: 15, centerLeft: 32, center: 50, centerRight: 68, right: 85 },
  landscape: { left: 15, centerLeft: 32, center: 50, centerRight: 68, right: 85 },
  portrait: { left: 22, centerLeft: 36, center: 50, centerRight: 64, right: 78 },
};

/** `vertical` as y in % of the stage height; `ground` puts a centre hinge on a line near the bottom. */
export const VERTICAL_Y: Readonly<Record<'top' | 'middle' | 'ground', number>> = { top: 25, middle: 50, ground: 70 };

/** `relativeTo` gaps in % of the stage width, narrower in portrait. */
export const GAP_X: Readonly<Record<Orientation, Readonly<Record<'close' | 'normal' | 'far', number>>>> = {
  desktop: { close: 12, normal: 20, far: 30 },
  landscape: { close: 12, normal: 20, far: 30 },
  portrait: { close: 10, normal: 15, far: 22 },
};

/** The store's position range (`clampSpriteAxis`). */
export const POSITION_RANGE = [-50, 150] as const;

const clampPos = (v: number) => Math.min(POSITION_RANGE[1], Math.max(POSITION_RANGE[0], v));
const round2 = (v: number) => Math.round(v * 100) / 100;

/**
 * The hinge position `{x, y}` in % for `placement` in `orientation`. For
 * `relativeTo` the reference is looked up in `doc`; a missing reference
 * resolves to the centre (the operations check it first and fail `not_found`).
 */
export function resolvePlacement(
  placement: Placement,
  doc: { sprites: readonly Sprite[] },
  _spriteId: string | undefined,
  orientation: Orientation,
): { x: number; y: number } {
  if ('at' in placement) {
    return {
      x: clampPos(AT_X[orientation][placement.at]),
      y: clampPos(VERTICAL_Y[placement.vertical ?? 'ground']),
    };
  }
  if ('relativeTo' in placement) {
    const ref = doc.sprites.find((s) => s.id === placement.relativeTo);
    const at = ref ? getResponsivePosition(ref.layout, orientation) : { x: 50, y: VERTICAL_Y.ground };
    const gap = GAP_X[orientation][placement.gap ?? 'normal'];
    return { x: clampPos(round2(at.x + (placement.side === 'rightOf' ? gap : -gap))), y: clampPos(at.y) };
  }
  return { x: clampPos(placement.x), y: clampPos(placement.y) };
}

/**
 * The position a base composition gives `orientation` from the basis
 * position alone (`resetSpriteOrientation`): x maps piecewise-linearly
 * through `AT_X[basis]` to `AT_X[orientation]` (exact at the five labels;
 * beyond the outer labels it keeps its distance from the nearest one), y is
 * unchanged, and the result is clamped like a placement.
 */
export function derivePosition(
  basisPosition: { x: number; y: number },
  basis: Orientation,
  orientation: Orientation,
): { x: number; y: number } {
  const from = AT_LABELS.map((l) => AT_X[basis][l]);
  const to = AT_LABELS.map((l) => AT_X[orientation][l]);
  const x = basisPosition.x;
  let out: number;
  if (x <= from[0]) out = to[0] - (from[0] - x);
  else if (x >= from[from.length - 1]) out = to[to.length - 1] + (x - from[from.length - 1]);
  else {
    let i = 0;
    while (x > from[i + 1]) i++;
    out = to[i] + ((x - from[i]) / (from[i + 1] - from[i])) * (to[i + 1] - to[i]);
  }
  return { x: clampPos(round2(out)), y: clampPos(basisPosition.y) };
}

/** The `at` label nearest to `x` in `orientation` (for the scene summary). */
export function nearestAtLabel(x: number, orientation: Orientation): AtLabel {
  const table = AT_X[orientation];
  let best: AtLabel = 'center';
  for (const label of Object.keys(table) as AtLabel[]) {
    if (Math.abs(table[label] - x) < Math.abs(table[best] - x)) best = label;
  }
  return best;
}

/**
 * Sprites from back to front: by zIndex, then authored order (the order
 * every renderer draws, spec 012).
 */
export function backToFront<T extends Pick<Sprite, 'zIndex'>>(sprites: readonly T[]): T[] {
  return sprites
    .map((s, i) => ({ s, i }))
    .sort((a, b) => (a.s.zIndex ?? 0) - (b.s.zIndex ?? 0) || a.i - b.i)
    .map(({ s }) => s);
}

/**
 * New zIndex values (by sprite id) that put `spriteId` directly in front of
 * or behind `refId`, all values distinct. When the other sprites' values are
 * already distinct, only the sprites in the way move up by one; otherwise
 * every sprite is first renumbered 1..n in its current order.
 */
export function depthZIndices(
  sprites: readonly Sprite[],
  spriteId: string,
  refId: string,
  where: 'inFrontOf' | 'behind',
): Map<string, number> {
  const z = new Map<string, number>();
  const others = sprites.filter((s) => s.id !== spriteId);
  const distinct = new Set(others.map((s) => s.zIndex ?? 0)).size === others.length;
  if (distinct) {
    for (const s of others) z.set(s.id, s.zIndex ?? 0);
  } else {
    backToFront(others).forEach((s, i) => z.set(s.id, i + 1));
  }
  const refZ = z.get(refId)!;
  const target = where === 'inFrontOf' ? refZ + 1 : refZ;
  // Push the chain of occupied slots upward from the target.
  const byZ = new Map<number, string>();
  for (const [id, v] of z) byZ.set(v, id);
  let slot = target;
  let moving = byZ.get(slot);
  const shifts: Array<[string, number]> = [];
  while (moving !== undefined) {
    shifts.push([moving, slot + 1]);
    slot += 1;
    moving = byZ.get(slot);
  }
  for (const [id, v] of shifts) z.set(id, v);
  z.set(spriteId, target);
  return z;
}
